import { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import type { Customer } from '../pages/customers/CustomerInterfaces';
import type { FeedEmailData } from '../pages/mail/components/EmailFeedContent';
import { useEmailPanel } from '../pages/mail/contexts/EmailPanelContext';
import {
  convertEmailDoc, correspondenceTimestamp, customerCorrespondenceAddresses,
  matchesCorrespondenceCustomer, mergeCorrespondence,
} from '../utils/customerCorrespondence';

export interface CustomerCorrespondenceOptions { limit?: number; enabled?: boolean }
export interface CustomerCorrespondenceResult {
  emails: FeedEmailData[];
  latest: FeedEmailData | null;
  loading: boolean;
  error: string | null;
  /** History is bounded; unlinked outbound mail is supplemented only from the loaded inbox. */
  partial: boolean;
}

interface SourceState { emails: FeedEmailData[]; loading: boolean; error: string | null; truncated: boolean }
interface QueryState { key: string; sources: Record<string, SourceState> }

/** Subscribe only for visible/selected customers. No global per-customer query fanout. */
export function useCustomerCorrespondence(
  customerId: string,
  customer?: Customer,
  options: CustomerCorrespondenceOptions = {},
): CustomerCorrespondenceResult {
  const { preLoadedEmails } = useEmailPanel();
  const enabled = options.enabled !== false && !!customerId;
  const pageSize = Math.max(1, Math.min(300, Math.floor(options.limit || 300)));
  const addressKey = JSON.stringify(customerCorrespondenceAddresses(customer));
  const addresses: string[] = useMemo(() => JSON.parse(addressKey), [addressKey]);
  const queryKey = JSON.stringify([enabled, customerId, pageSize, addressKey]);
  const [state, setState] = useState<QueryState>({ key: '', sources: {} });

  useEffect(() => {
    if (!enabled) { setState({ key: queryKey, sources: {} }); return; }
    const sourceQueries = [
      { name: 'Linked correspondence', constraint: where('customerId', '==', customerId) },
      { name: 'Shared correspondence', constraint: where('customerId', 'array-contains', customerId) },
    ];
    // Batching addresses preserves secondary contacts without silently dropping those after #10.
    for (let start = 0; start < addresses.length; start += 10) {
      sourceQueries.push({ name: `Sender correspondence ${start / 10 + 1}`, constraint: where('senderEmail', 'in', addresses.slice(start, start + 10)) });
    }
    const initial: Record<string, SourceState> = {};
    for (const { name } of sourceQueries) initial[name] = { emails: [], loading: true, error: null, truncated: false };
    setState({ key: queryKey, sources: initial });
    let current = true;
    const unsubscribers = sourceQueries.map(({ name, constraint }) => onSnapshot(
      query(collection(db, 'emails'), constraint, orderBy('date', 'desc'), limit(pageSize)),
      snapshot => {
        if (!current) return;
        const source = { emails: snapshot.docs.map(doc => convertEmailDoc(doc.id, doc.data())), loading: false, error: null, truncated: snapshot.size >= pageSize };
        setState(previous => previous.key === queryKey ? { key: queryKey, sources: { ...previous.sources, [name]: source } } : previous);
      },
      error => {
        if (!current) return;
        setState(previous => previous.key === queryKey ? {
          key: queryKey, sources: { ...previous.sources, [name]: { emails: [], loading: false, error: `${name}: ${error.message}`, truncated: false } },
        } : previous);
      },
    ));
    return () => { current = false; unsubscribers.forEach(unsubscribe => unsubscribe()); };
  }, [enabled, customerId, pageSize, addresses, queryKey]);

  return useMemo(() => {
    if (!enabled) return { emails: [], latest: null, loading: false, error: null, partial: false };
    const sources = state.key === queryKey ? Object.values(state.sources) : [];
    const loaded = preLoadedEmails.filter(email => matchesCorrespondenceCustomer(email, customerId, addresses))
      .map(email => convertEmailDoc(email.id, email));
    const emails = mergeCorrespondence([...sources.map(source => source.emails), loaded]);
    const failures = sources.map(source => source.error).filter(Boolean);
    return {
      emails,
      latest: emails.find(email => correspondenceTimestamp(email) > 0) || null,
      loading: state.key !== queryKey || sources.some(source => source.loading),
      error: failures.length ? failures.join('; ') : null,
      partial: addresses.length > 0 || sources.some(source => source.truncated || !!source.error)
        || emails.some(email => !correspondenceTimestamp(email)),
    };
  }, [enabled, state, queryKey, preLoadedEmails, customerId, addresses]);
}
